import * as cookie from 'js/lib/cookie';
import requestCountry from 'js/lib/requestCountry';

import {
  EMAIL_CONSTRAINT,
  FULLNAME_CONSTRAINT,
  PASSWORD_CONSTRAINT,
  TOU_CHECKBOX_CONSTRAINT,
} from 'bundles/authentication-common/constants/constraints';
import localStorageEx from 'bundles/common/utils/localStorageEx';

declare const COURSERA_APP_NAME: string;

const EXCLUDED_APPS_FOR_LEGAL_CONSOLIDATION_AUTH_FLOW = [
  'front-page',
  'consumer-home',
  'authentication',
  'enterprise-home',
  'enterprise-description-page',
];

const isExcludedAppForLegalConsolidationAuthFlow = () => {
  const appName = COURSERA_APP_NAME;
  return EXCLUDED_APPS_FOR_LEGAL_CONSOLIDATION_AUTH_FLOW.includes(appName);
};

/**
 * Extract user email from _ue cookie and delete it immediately.
 * This is used to prefill the email field in the login form when the user is redirected with SSO error.
 *
 * @returns The user email from the cookie or undefined if the cookie is not found.
 */
export const getUserEmailFromCookie = (): string | undefined => {
  const cookieName = '_ue';
  const userEmail = cookie.get(cookieName);

  if (userEmail) {
    // Delete the cookie immediately after reading
    cookie.remove(cookieName);
    return userEmail;
  }

  return undefined;
};

// For automation tests like E2E, we need to bypass the bot manager flow, except for test cases that include Arkose.
export const shouldBypassBotManagerForAutomationTest = () =>
  localStorageEx.getItem('shouldBypassBotManager', Boolean, false);

// Utility function to determine when to show the terms of use acceptance flow this is for all US users
export const isTermsOfUseRolloutForUS = () => {
  return requestCountry.get() === 'US';
};

// Utility function to check if the legal consolidation auth flow should be shown to the user
export const shouldShowLegalConsolidationAuthFlow = (isUVRegisteredOnboardingSignupFlow = false) => {
  return (
    requestCountry.get() === 'US' &&
    (!isExcludedAppForLegalConsolidationAuthFlow() || isUVRegisteredOnboardingSignupFlow)
  );
};

// Utility function to check if the checkbox terms of use should be shown in the signup form
export const shouldShowCheckboxTermsOfUse = () => {
  return !shouldShowLegalConsolidationAuthFlow() && isTermsOfUseRolloutForUS();
};

export const shouldShowTOSDisclaimer = () => !shouldShowCheckboxTermsOfUse() && !shouldShowLegalConsolidationAuthFlow();

export const BOT_MANAGER_UNSERIALIZABLE_ERROR_MESSAGE = 'Unserializable bot manager error';
/**
 * Safely derives a loggable message from a caught bot manager error.
 * Third-party bot detection scripts (e.g. Arkose) can reject with non-Error
 * values, including DOM nodes/events. Those hold circular references (via
 * React fiber back-pointers), so JSON.stringify throws; this guards against that.
 */
export const getBotManagerErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  try {
    const serialized = JSON.stringify(error);
    return serialized ?? BOT_MANAGER_UNSERIALIZABLE_ERROR_MESSAGE;
  } catch {
    return BOT_MANAGER_UNSERIALIZABLE_ERROR_MESSAGE;
  }
};

// Utility function to determine whether to show the progressive (unified) authentication flow.
// Progressive auth is the default experience; specific flows (enterprise, program, internal tools,
// third-party auth) opt out by passing shouldDisableProgressiveAuth.
export const shouldShowProgressiveAuth = ({
  shouldDisableProgressiveAuth,
}: {
  shouldDisableProgressiveAuth: boolean;
}) => !shouldDisableProgressiveAuth;

/**
 * Returns validation constraints for the signup form based on whether it's an enterprise SSO login.
 * Each constraint is a function that takes a string as an argument and returns a string
 * (indicating an error message) or a boolean (indicating that the input is valid).
 *
 * @param isEnterpriseSsoLogin - Whether this is an enterprise SSO login
 * @param showCheckboxTermsOfUse - Whether to show and validate the terms of use checkbox
 * @returns {Record<string, (arg: string) => string | false>} The validation constraints
 */
export const getSignupFormValidationConstraints = (
  isEnterpriseSsoLogin: boolean,
  showCheckboxTermsOfUse: boolean
): Record<string, (value: string) => string | false> => {
  const enterpriseValidationConstraints = {
    email: EMAIL_CONSTRAINT,
  };

  return isEnterpriseSsoLogin
    ? enterpriseValidationConstraints
    : {
        name: FULLNAME_CONSTRAINT,
        email: EMAIL_CONSTRAINT,
        password: PASSWORD_CONSTRAINT,
        touCheckbox: showCheckboxTermsOfUse ? TOU_CHECKBOX_CONSTRAINT : (_value: string) => false,
      };
};
